import {ReactNode, useState, useEffect, useContext, useMemo} from 'react';

import {useTranslation} from 'react-i18next';

import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import SettingsIcon from '@mui/icons-material/Settings';
import TranslateOutlinedIcon from '@mui/icons-material/TranslateOutlined';
import TuneIcon from '@mui/icons-material/Tune';
import {Box, Grid, SxProps, Typography} from '@mui/material';

import {SurveyManagmentModal} from '../modals/SurveyManagmentModal';

import {SurveyButtons} from './buttons/SurveyButtons';
import HandleNoSurveys from './chart/HandleNoSurveys';
import StatisticNumbers from './statistic-details/StatisticNumbers';
import {SurveyPaperHeader} from './SurveyPaperHeader';
import {ConfirmDeleteDialog} from '../../../../../../components/ConfirmDeleteDialog/ConfirmDeleteDialog';
import {EmpionButton} from '../../../../../../components/EmpionButton/EmpionButton';
import EmpionDialog from '../../../../../../components/EmpionDialog/EmpionDialog';
import {EmpionPaper} from '../../../../../../components/EmpionPaper/EmpionPaper';
import {Loading} from '../../../../../../components/Loading/Loading';
import {CompanyContext} from '../../../../../../contexts/CompanyContext/CompanyContext';
import {LoadingContext} from '../../../../../../contexts/LoadingContext/LoadingContext';
import {SnackBarContext} from '../../../../../../contexts/SnackBarContext/SnackBarContext';
import {useSurvey} from '../../../../../../contexts/SurveyContext/SurveyContext';
import {useApiWrite} from '../../../../../../hooks/useApiWrite';
import {orangeColors} from '../../../../../../layouts/theme/designSystem';
import {Delete} from '../../../../../../lib/api/DELETE/Delete';
import {DeleteRoutes} from '../../../../../../lib/api/DELETE/DeleteRoutes';
import {SurveyService} from '../../services/SurveyService';
import {Survey} from '../../types/SurveyTypes';

interface Props {
  upComingCultureSurvey: Survey | undefined;
  currentCultureSurvey: Survey | undefined;
  previousCultureSurvey: Survey | undefined;
  previousCultureSurveys?: Survey[];
  loading: boolean;
}

export type SurveyState = 'current' | 'upcoming' | 'previous';

export const SurveyPaperWrapper = ({
  upComingCultureSurvey,
  currentCultureSurvey,
  previousCultureSurvey,
  previousCultureSurveys = [],
  loading,
}: Props) => {
  const {t} = useTranslation();
  const {dispatchMessage} = useContext(SnackBarContext);
  const {setLoadingContext} = useContext(LoadingContext);
  const {company} = useContext(CompanyContext);
  const {fetchSurveys} = useSurvey();
  const companyId = company.data?.resourceId || '';
  const [stateCultureSurvey, setStateCultureSurvey] = useState<SurveyState | null>(null);

  const [stateDetailsCultureSurvey, setStateDetailsCultureSurvey] = useState<SurveyState | null>(null);
  const [openCulture, setOpenCulture] = useState<boolean>(false);
  const [openDiscard, setOpenDiscard] = useState(false);
  const [openComplete, setOpenComplete] = useState(false);
  const [surveyToDiscard, setSurveyToDiscard] = useState<Survey | null>(null);

  const surveyService = useMemo(() => new SurveyService(), []);
  const allowClosingSurvey =
    !!currentCultureSurvey?.statistics?.employeeRespondents && !!currentCultureSurvey?.statistics?.managerRespondents;

  const {makeManualRequestWithParams: deleteSurvey, error: deleteError} = useApiWrite({
    method: 'delete',
    onSuccess: () => {
      dispatchMessage(t('shared.formSubmit.success'), 'success');
      fetchSurveys();
    },
  });

  useEffect(() => {
    const handleCultureSurveyState = () => {
      if (currentCultureSurvey) {
        setStateCultureSurvey('current');
      } else if (upComingCultureSurvey) {
        setStateCultureSurvey('upcoming');
      } else if (previousCultureSurvey) {
        setStateCultureSurvey('previous');
      } else {
        setStateCultureSurvey(null);
      }
    };

    const handleDetailsCultureSurveyState = () => {
      if (currentCultureSurvey) {
        setStateDetailsCultureSurvey('current');
      } else if (upComingCultureSurvey) {
        setStateDetailsCultureSurvey('upcoming');
      } else if (previousCultureSurvey) {
        setStateDetailsCultureSurvey('previous');
      } else {
        setStateDetailsCultureSurvey(null);
      }
    };

    handleDetailsCultureSurveyState();
    handleCultureSurveyState();
  }, [upComingCultureSurvey, currentCultureSurvey, previousCultureSurvey]);

  useEffect(() => {
    if (deleteError) {
      dispatchMessage(t('shared.formSubmit.error'), 'error');
    }
  }, [deleteError, dispatchMessage, t]);

  const paperStyles: SxProps = {
    width: '100%',
    padding: {xs: '1rem', sm: '1.5rem'},
    borderRadius: '12px',
    backgroundColor: '#FFFFFF',
    border: `0.5px solid ${orangeColors[3]}`,
  };

  const handleSurvey = (survey: Survey | undefined) => {
    if (survey) return survey;
    return undefined;
  };

  const handleCloseCulture = () => setOpenCulture(false);

  const renderConfigurationColumn = (icon: ReactNode, title: string, items: string[]) => (
    <Box
      sx={{
        backgroundColor: orangeColors[7],
        borderRadius: '8px',
        padding: '1rem',
        height: '100%',
      }}
    >
      <Box sx={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '0.75rem'}}>
        {icon}
        <Typography
          sx={{
            fontSize: '11px',
            fontWeight: 700,
            letterSpacing: '0.04em',
            lineHeight: 1.4,
            textTransform: 'uppercase',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Grid container flexDirection='column' gap={0.75}>
        {items.map((item) => (
          <Grid item key={item} sx={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <RadioButtonUncheckedIcon sx={{fontSize: 16}} />
            <Typography sx={{fontSize: '13px', lineHeight: 1.4}}>{item}</Typography>
          </Grid>
        ))}
      </Grid>
    </Box>
  );

  const renderCurrentSurveyEmptyState = () => (
    <Grid container flexDirection='column' gap={3}>
      <Box>
        <Typography variant='h4' sx={{marginBottom: 1}}>
          {t('companySurveys.details.noCurrentSurveyTitle')}
        </Typography>
        <Typography variant='body1'>{t('companySurveys.details.noCurrentSurveyDescription')}</Typography>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gap: '1rem',
          gridTemplateColumns: {xs: '1fr', md: 'repeat(3, minmax(0, 1fr))'},
        }}
      >
        {renderConfigurationColumn(
          <TranslateOutlinedIcon sx={{fontSize: 18}} />,
          t('companySurveys.modals.defaultLanguage'),
          [t('shared.languages.de'), t('shared.languages.en')]
        )}
        {renderConfigurationColumn(
          <TuneIcon sx={{fontSize: 18}} />,
          t('companySurveys.modals.questionnaireConfiguration'),
          [
            t('companySurveys.modals.omitDepartment'),
            t('companySurveys.modals.omitSpokenLanguage'),
            t('companySurveys.modals.omitGender'),
            t('companySurveys.modals.omitLocation'),
            t('companySurveys.modals.omitAgeGroup'),
            t('companySurveys.modals.omitWorkMode'),
            t('companySurveys.modals.omitFeedback'),
          ]
        )}
        {renderConfigurationColumn(
          <CalendarTodayOutlinedIcon sx={{fontSize: 18}} />,
          t('companySurveys.modals.dateRangeTitle'),
          [t('companySurveys.modals.startDate'), t('companySurveys.modals.endDate')]
        )}
      </Box>

      <Grid item alignSelf='flex-end' data-tour='surveys.configure'>
        <EmpionButton
          onClick={() => handleOpen('culture')}
          startIcon={<SettingsIcon fontSize='small' />}
          variant='primary'
        >
          {t('companySurveys.actionButtons.configure')}
        </EmpionButton>
      </Grid>
    </Grid>
  );

  const handleConfirmDiscard = () => {
    setLoadingContext(true);
    if (!surveyToDiscard?.resourceId) {
      setLoadingContext(false);
      return;
    }

    deleteSurvey({
      method: 'delete',
      path: DeleteRoutes[Delete.DeleteSurvey](companyId, surveyToDiscard.resourceId),
    });
    setOpenDiscard(false);
    setSurveyToDiscard(null);
    setLoadingContext(false);
  };

  const handleOpenDiscard = (survey: Survey) => {
    setSurveyToDiscard(survey);
    setOpenDiscard(true);
  };

  const handleConfirmComplete = async () => {
    setLoadingContext(true);
    if (!currentCultureSurvey?.resourceId) {
      setLoadingContext(false);
      return;
    }

    const res = await surveyService.completeSurvey(companyId, currentCultureSurvey.resourceId);
    if (res?.ok) {
      dispatchMessage(t('shared.formSubmit.success'), 'success');
      fetchSurveys();
    } else {
      dispatchMessage(t('shared.formSubmit.error'), 'error');
    }

    setOpenComplete(false);
    setLoadingContext(false);
  };

  const handleOpen = (surveyType: string) => {
    if (surveyType === 'culture') {
      setOpenCulture(true);
    }
  };

  const handleShowSurveyDetails = (state: string | null) => {
    if (state === 'current') {
      return handleSurvey(currentCultureSurvey);
    }
    if (state === 'upcoming') {
      return handleSurvey(upComingCultureSurvey);
    }
    return undefined;
  };

  const detailsSurvey = handleSurvey(currentCultureSurvey ? currentCultureSurvey : upComingCultureSurvey);
  const showPreviousSurveysSection = previousCultureSurveys.length > 0;

  const renderSurveyDetailsCard = (
    survey: Survey | undefined,
    state: SurveyState | null,
    showButtons = false,
    showEmptyState = false
  ) => (
    <EmpionPaper sx={paperStyles}>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '100%',
            minHeight: {xs: '30vh', sm: '40vh', md: '50vh'},
          }}
        >
          <Loading />
        </Box>
      ) : (
        <Grid container flexDirection='column'>
          {survey ? (
            <StatisticNumbers survey={survey} state={state} showCultureTypeDistribution={state === 'previous'} />
          ) : showEmptyState ? (
            renderCurrentSurveyEmptyState()
          ) : (
            <HandleNoSurveys handleOpen={() => handleOpen('culture')} />
          )}

          {showButtons && survey ? (
            <Grid item alignSelf='flex-end' mt={2} data-tour='surveys.configure'>
              <SurveyButtons
                state={state}
                survey={survey}
                allowComplete={allowClosingSurvey}
                onConfigure={() => handleOpen('culture')}
                onComplete={state === 'current' ? () => setOpenComplete(true) : undefined}
                onDiscard={() => handleOpenDiscard(survey)}
              />
            </Grid>
          ) : null}
        </Grid>
      )}
    </EmpionPaper>
  );

  return (
    <>
      {/* Culture survey */}
      <SurveyPaperHeader
        survey={handleSurvey(currentCultureSurvey ? currentCultureSurvey : upComingCultureSurvey)}
        state={stateCultureSurvey}
      />
      <Box data-tour='surveys.overview'>
        {renderSurveyDetailsCard(detailsSurvey, stateDetailsCultureSurvey, Boolean(detailsSurvey), true)}
      </Box>

      {showPreviousSurveysSection ? (
        <Box sx={{marginTop: 4}}>
          <Typography variant='h3' sx={{marginBottom: 2}}>
            {t('companySurveys.details.previousCultureSurveys')}
          </Typography>
          <Grid container flexDirection='column' gap={2}>
            {previousCultureSurveys.map((survey) => (
              <Grid item key={survey.resourceId}>
                {renderSurveyDetailsCard(survey, 'previous')}
              </Grid>
            ))}
          </Grid>
        </Box>
      ) : null}

      <EmpionDialog
        onClose={handleCloseCulture}
        open={openCulture}
        sx={{
          '& .MuiDialog-paper': {
            width: {xs: '100%', sm: '704px'},
            backgroundColor: 'white',
            backgroundImage: 'none',
          },
        }}
      >
        <SurveyManagmentModal
          handleClose={handleCloseCulture}
          type={'culture'}
          state={stateCultureSurvey}
          handleShowSurveyDetails={handleShowSurveyDetails}
        />
      </EmpionDialog>
      <ConfirmDeleteDialog
        open={openDiscard}
        dialogContent={t('companySurveys.actionButtons.discardMsg')}
        handleConfirm={handleConfirmDiscard}
        handleCancel={() => {
          setOpenDiscard(false);
          setSurveyToDiscard(null);
        }}
      />
      <ConfirmDeleteDialog
        open={openComplete}
        dialogContent={t('companySurveys.actionButtons.completionMsg')}
        handleConfirm={handleConfirmComplete}
        handleCancel={() => setOpenComplete(false)}
      />
    </>
  );
};
